import { CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, Input, Popover } from 'antd';
import { forwardRef, useImperativeHandle, useState } from 'react';

import { ResponsibleDropdown } from './components/ResponsibleDropdown';
import { TaskDatePicker } from './components/TaskDatePicker';
import { TaskTypeDropdown } from './components/TaskTypeDropdown';
import { useTaskComposer } from './hooks/useTaskComposer';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import './TaskComposer.scss';

const { TextArea } = Input;

export interface TaskComposerRef {
  submit(): Promise<boolean>;
}

interface TaskComposerProps {
  value: string;
  onChange(value: string): void;
  clientId: number | null;
  onSuccess(): void;
}

export const TaskComposer = forwardRef<TaskComposerRef, TaskComposerProps>(
  ({ value, onChange, clientId, onSuccess }, ref) => {
    const [isDateOpen, setIsDateOpen] = useState(false);
    const [isResponsibleOpen, setIsResponsibleOpen] = useState(false);
    const [isTypeOpen, setIsTypeOpen] = useState(false);

    const {
      chipLabel,
      startDate,
      endDate,
      preset,
      selectionPhase,
      previewStart,
      previewEnd,
      viewMonth,
      selectPreset,
      handleDayClick,
      handleDayHover,
      finishDateSelection,
      setViewMonth,
      isAllDay,
      startTime,
      endTime,
      toggleAllDay,
      handleStartTimeChange,
      handleEndTimeChange,
      handleSetDuration,
      selectedExecutor,
      responsibleLabel,
      isResponsibleMuted,
      selectExecutor,
      taskTypes,
      selectedTaskType,
      selectTaskType,
      createTaskType,
      editTaskType,
      deleteTaskType,
      taskTypeLabel,
      taskTypeIconId,
      taskTypeColor,
      isTaskTypeMuted,
      executors,
      handleSubmit,
    } = useTaskComposer({ clientId });

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          const ok = await handleSubmit(value);
          if (ok) onSuccess();
          return ok;
        },
      }),
      [handleSubmit, value, onSuccess]
    );

    const dateOpenChangeHandler = (open: boolean) => {
      if (!open) finishDateSelection();
      setIsDateOpen(open);
    };

    const datePicker = (
      <TaskDatePicker
        startDate={startDate}
        endDate={endDate}
        preset={preset}
        selectionPhase={selectionPhase}
        previewStart={previewStart}
        previewEnd={previewEnd}
        viewMonth={viewMonth}
        isAllDay={isAllDay}
        startTime={startTime}
        endTime={endTime}
        onPresetSelect={selectPreset}
        onDayClick={handleDayClick}
        onDayHover={handleDayHover}
        onViewMonthChange={setViewMonth}
        onToggleAllDay={toggleAllDay}
        onStartTimeChange={handleStartTimeChange}
        onEndTimeChange={handleEndTimeChange}
        onSetDuration={handleSetDuration}
        onClose={() => dateOpenChangeHandler(false)}
      />
    );

    const responsibleDropdown = (
      <ResponsibleDropdown
        executors={executors}
        selectedId={selectedExecutor?.id ?? null}
        onSelect={selectExecutor}
        onClose={() => setIsResponsibleOpen(false)}
      />
    );

    return (
      <div className="TaskComposer">
        <div className="TaskComposer__chips">
          <Popover
            content={datePicker}
            trigger="click"
            placement="top"
            open={isDateOpen}
            onOpenChange={dateOpenChangeHandler}
            arrow={false}
            overlayInnerStyle={{ padding: 0 }}
          >
            <button
              type="button"
              className={[
                'TaskComposer__chip',
                isDateOpen && 'TaskComposer__chip--open',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <CalendarOutlined className="TaskComposer__chip-icon" />
              <span>{chipLabel}</span>
            </button>
          </Popover>

          <Dropdown
            trigger={['click']}
            open={isResponsibleOpen}
            onOpenChange={setIsResponsibleOpen}
            placement="topLeft"
            popupRender={() => responsibleDropdown}
          >
            <button
              type="button"
              className={[
                'TaskComposer__chip',
                isResponsibleMuted && 'TaskComposer__chip--muted',
                isResponsibleOpen && 'TaskComposer__chip--open',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <UserOutlined className="TaskComposer__chip-icon" />
              <span>{responsibleLabel}</span>
            </button>
          </Dropdown>

          <Dropdown
            trigger={['click']}
            open={isTypeOpen}
            onOpenChange={setIsTypeOpen}
            placement="topLeft"
            destroyOnHidden
            popupRender={() => (
              <TaskTypeDropdown
                taskTypes={taskTypes}
                selectedId={selectedTaskType?.Id ?? null}
                onSelect={selectTaskType}
                onCreate={createTaskType}
                onEdit={editTaskType}
                onDelete={deleteTaskType}
                onClose={() => setIsTypeOpen(false)}
              />
            )}
          >
            <button
              type="button"
              className={[
                'TaskComposer__chip',
                isTaskTypeMuted && 'TaskComposer__chip--muted',
                isTypeOpen && 'TaskComposer__chip--open',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {taskTypeIconId ? (
                <FontAwesomeIcon
                  className="TaskComposer__chip-icon"
                  icon={taskTypeIconId as IconProp}
                  style={{ color: taskTypeColor || undefined }}
                />
              ) : null}
              <span>{taskTypeLabel}</span>
            </button>
          </Dropdown>
        </div>

        <TextArea
          className="TaskComposer__textarea"
          autoSize={{ minRows: 1, maxRows: 3 }}
          placeholder="Введите текст задачи"
          value={value}
          onChange={({ target: { value: v } }) => onChange(v)}
        />
      </div>
    );
  }
);
